import {NumberFormat, SortableColumn} from "@src/components";
import React from "react";
import {createColumnHelper} from "@tanstack/react-table";
import {ProcessFilterRequestType, ProcessType} from "@process/types/process.types";
import {Link} from "react-router-dom";
import {ProcessSalonStatusKeyEnum} from "@process/types/process.enums";
import {toDateTimeFormat} from "@src/helpers/utils.helper";
import {EstimateStatusKeyEnum} from "@estimate/types/estimate.enums";
import {Array, ColumnDefType} from "@src/types/global.types";
import {OverlayTrigger, Tooltip} from "react-bootstrap";

const columnHelper = createColumnHelper<ProcessType>()

export const defaultColumns = ['date', 'number', 'title', 'salonStatus', 'serviceStatus', 'user', 'customer', 'totalPriceGross'];
export const  columnDefinitions: Array<ColumnDefType> = [
    { id: 'date', name: 'Data', visible: false},
    { id: 'department', name: 'Oddział', visible: false},
    { id: 'number', name: 'Numer', visible: false},
    { id: 'title', name: 'Tytuł', visible: false},
    { id: 'salonStatus', name: 'Status salonu', visible: false},
    { id: 'serviceStatus', name: 'Status serwisu', visible: false},
    { id: 'user', name: 'Przypisane do', visible: false},
    { id: 'customer', name: 'Klient', visible: false},
    { id: 'totalPriceGross', name: 'Wycena', visible: false},
    { id: 'invoiceNumber', name: 'Numer FV/paragonu', visible: false},
    { id: 'resignationAt', name: 'Data rezygnacji', visible: false},
    { id: 'closedAt', name: 'Data zakończenia', visible: false},
    { id: 'paidAt', name: 'Data płatności', visible: false},
    { id: 'divisionName', name: 'Dział', visible: false},
];

export const getAvailableColumns = (processFilter: ProcessFilterRequestType): any[] => {
    const visibleColumnDef = processFilter.column.map(colId => {
        let col: ColumnDefType = columnDefinitions.find(c => c.id === colId);
        return {...col, visible: true};
    });
    const hiddenColumnDef = columnDefinitions.filter(col => !processFilter.column.includes(col.id));

    return [...visibleColumnDef, ...hiddenColumnDef];
}


export const getProcessColumn = (processFilter: ProcessFilterRequestType) => {
    return [
        columnHelper.accessor(process => process.date, {
            id: 'date',
            header: () => <SortableColumn label="Data" orderKey="createdAt" params={processFilter}/>,
        }),
        columnHelper.accessor(process => process, {
            id: 'number',
            header: () => <SortableColumn label="Numer" orderKey="number" params={processFilter}/>,
            cell: info => <Link to={`/process/${info.getValue().id}`}>{info.getValue().number}</Link>
        }),
        columnHelper.accessor(process => process, {
            id: 'title',
            header: () => <SortableColumn label="Tytuł" orderKey="title" params={processFilter}/>,
            cell: info => {
                const process = info.getValue();
                return (
                    <div className="d-flex justify-content-between">
                        <Link to={`/process/${process.id}`}>{process.title}</Link>
                        {process?.items[0]?.substitute && (
                            <OverlayTrigger
                                overlay={(props) => (
                                    <Tooltip {...props}>
                                        <div>Model: {process.items[0].substitute.name}</div>
                                        <div>Data wydania: {process.items[0].substitute.issueDate}</div>
                                        <div>Data zwrotu: {process.items[0].substitute.returnDate ?? '- - -'}</div>
                                    </Tooltip>
                                )}
                            >
                                <div>
                                    <span className={`badge ${ process.items[0].substitute.returnDate ? 'bg-success-lt' : 'bg-purple-lt'}`}>
                                        ZASTĘPCZY
                                    </span>
                                </div>
                            </OverlayTrigger>
                        )}
                    </div>
                )
            }
        }),
        columnHelper.accessor(process => process.department, {
            id: 'department',
            header: () => 'Oddział',
            cell: info => {
                const department = info.getValue();
                return `${department.code} - ${department.name}`;
            },
        }),
        columnHelper.accessor(process => process.division, {
            id: 'divisionName',
            header: () => 'Dział',
            cell: info => {
                return info?.getValue().name ?? '';
            },
            meta: {
                className: 'text-center'
            }
        }),
        columnHelper.accessor(process => process, {
            id: 'salonStatus',
            header: () => <SortableColumn label="Status salonu" orderKey="salonStatus" params={processFilter}/>,
            cell: info => {
                const process = info.getValue();
                return (
                    <>
                        <div>{process.salonStatus.name}</div>
                        {(process.salonStatus.key === ProcessSalonStatusKeyEnum.Done && process.closedAt) && (
                            <div className="small text-muted">{toDateTimeFormat(process.closedAt)}</div>
                        )}
                    </>
                )
            },
            meta: {
                className: 'text-center'
            }
        }),
        columnHelper.accessor(process => process, {
            id: 'serviceStatus',
            header: () => <SortableColumn label="Status serwisu" orderKey="serviceStatus" params={processFilter}/>,
            cell: info => {
                const process = info.getValue();
                return (process.serviceStatus?.name ?? <span className="text-muted">- - -</span>)
            },
            meta: {
                className: 'text-center'
            }
        }),
        columnHelper.accessor(process => process.user, {
            id: 'user',
            header: () => <SortableColumn label="Przypisane do" orderKey="user" params={processFilter}/>,
            cell: info => `${info.getValue().firstName} ${info.getValue().lastName}`
        }),
        columnHelper.accessor(process => process.invoiceNumber, {
            id: 'invoiceNumber',
            header: () => 'Numer FV/paragonu',
            // cell: info => `${info.getValue(). ?? ''}`
        }),
        columnHelper.accessor(process => process.customer, {
            id: 'customer',
            header: () => <SortableColumn label="Klient" orderKey="customer" params={processFilter}/>,
            cell: info => {
                const customer = info.getValue();
                return customer.companyName
                    ? customer.companyName
                    : `${customer.firstName ?? ''} ${customer.lastName ?? ''}`
            },
        }),
        columnHelper.accessor(process => process, {
            id: 'totalPriceGross',
            header: () => <SortableColumn label="Wycena" orderKey="totalPriceGross" params={processFilter}/>,
            cell: info => {
                const process = info.getValue();
                return (
                    <>
                        {process.estimate?.status?.key === EstimateStatusKeyEnum.Accepted && (
                            <span className="badge bg-success me-1"></span>
                        )}
                        {process.estimate?.status?.key === EstimateStatusKeyEnum.Rejected && (
                            <span className="badge bg-danger me-1"></span>
                        )}
                        {process.estimate?.status?.key === EstimateStatusKeyEnum.Sent && (
                            <span className="badge bg-primary me-1"></span>
                        )}

                        {process.estimate
                            ? <Link to={`/estimate/${process.estimate.id}`}><NumberFormat
                                value={process.totalPriceGross}/></Link>
                            : <NumberFormat value={process.totalPriceGross}/>
                        }
                    </>
                )
            },
            meta: {
                className: 'text-end'
            }
        }),
        columnHelper.accessor(process => process.resignationAt, {
            id: 'resignationAt',
            header: () => <SortableColumn label="Data rezygnacji" orderKey="resignationAt" params={processFilter}/>,
            cell: info => toDateTimeFormat(info.getValue()),
            meta: { className: 'text-center' },
        }),
        columnHelper.accessor(process => process.closedAt, {
            id: 'closedAt',
            header: () => <SortableColumn label="Data zakończenia" orderKey="closedAt" params={processFilter}/>,
            cell: info => toDateTimeFormat(info.getValue()),
            meta: { className: 'text-center' },
        }),
        columnHelper.accessor(process => process.paidAt, {
            id: 'paidAt',
            header: () => <SortableColumn label="Data płatności" orderKey="paidAt" params={processFilter}/>,
            cell: info => toDateTimeFormat(info.getValue()),
            meta: { className: 'text-center' },
        }),
    ]
}